import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DistributorID } from 'fym-shared/src/smartlinks/catalog';
import { StreamingOverviewResponse, StreamingTrackRow } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { useStreamingTracks } from '../hooks/useStreamingTracks';
import { streamingTrackPath } from '../streamingPaths';
import { sourceLook } from '../streamingSources';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { streamingMessages } from '../streamingMessages';
import { StreamingBlock } from '../parts/StreamingBlock';
import { TracksTable } from '../parts/TracksTable';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TracksSectionProps {
  accountId: number;
  overview: StreamingOverviewResponse;
  onNavigate: (path: string) => void;
}

/** Streaming → Tracks (design d1). The Net earnings column shows only to a caller who may see money. */
const TracksSectionComponent: React.FC<TracksSectionProps> = ({ accountId, overview, onNavigate }) => {
  const intl = useIntl();
  const { data, isLoading, error, retry } = useStreamingTracks(accountId, overview.days);
  const tracks = useMemo(() => data?.tracks ?? [], [data]);
  const portalNames = useMemo(
    () =>
      overview.sources
        .filter((source) => source.kind === 'portal')
        .map((source) => sourceLook(source.integrationType).name)
        .join(', '),
    [overview.sources]
  );
  const open = useCallback(
    (track: StreamingTrackRow) => onNavigate(streamingTrackPath(track.key, overview.days)),
    [onNavigate, overview.days]
  );

  return (
    <StreamingBlock
      title={intl.formatMessage(streamingMessages.chipTracks)}
      meta={intl.formatMessage(messages.tracksCount, { count: tracks.length })}
      isLoading={isLoading}
      error={error}
      retry={retry}
      testId="streaming-section-tracks"
    >
      {tracks.length === 0 ? (
        <p className={styles.faint}>{intl.formatMessage(messages.emptyList)}</p>
      ) : (
        <TracksTable
          tracks={tracks}
          earningsHidden={data?.earningsHidden ?? true}
          earningsLockedByPlan={data?.hiddenReason === 'plan'}
          onOpen={open}
        />
      )}
      <p className={styles.footnote}>
        {overview.hasDistributor
          ? intl.formatMessage(messages.tracksNoteDistributor, { source: sourceLook(DistributorID.Ditto).shortName })
          : intl.formatMessage(messages.tracksNotePortals, { sources: portalNames })}{' '}
        {intl.formatMessage(messages.tracksNoteSaves)}
      </p>
    </StreamingBlock>
  );
};

export const TracksSection = TypedMemo(TracksSectionComponent);
